import { autoinject } from 'aurelia-framework';

import {
	APIParameters,
	DownloadSpec,
	GenericApi,
	ParamType,
} from '~/api/generic-api';
import { ReportStatusResponse } from '~/models/report-model';
import { FetchUtils } from '~/utils/fetch-utils';

/**
 * This should correspond to the Java class:
 * au.com.finsyn.acuritywebserver.core.resources.reports.ReportResource.ReportResponse
 */
export interface ReportGroupCodeDescription {
	reportCode: string;
	reportDescription: string;
}

/**
 * This should correspond to the Java class:
 * au.com.finsyn.acuritywebserver.core.resources.reports.ReportCodesResource.ReportGroupResponse
 */
export interface ReportGroupCodeAndDescription {
	reportGroupCode: string;
	reportGroupDescription: string;
}

/**
 * This should correspond to the Java class:
 * au.com.finsyn.acuritywebserver.core.api.DynamicFieldsConfig
 */
export interface DynamicFieldsConfig {
	fieldOrder: string[];
}

export interface GetReportCodesOptions {
	shouldRefresh: boolean;
}

@autoinject
export class ReportApi extends GenericApi {
	public async getAllReports(
		options: GetReportCodesOptions = { shouldRefresh: false },
	): Promise<readonly ReportGroupCodeDescription[]> {
		const url =
			GenericApi.apiServerURL +
			'api/report-codes' +
			'?' +
			GenericApi.getParamsString(
				{
					shouldRefresh: String(options.shouldRefresh),
				},
				[ParamType.FORM, ParamType.DIVISION],
			);

		return await FetchUtils.getJSONWithParamsInURL<
			readonly ReportGroupCodeDescription[]
		>(url);
	}

	public getReportGroupCodesAndDescriptions(): Promise<
		readonly ReportGroupCodeAndDescription[]
	> {
		return FetchUtils.getJSON<readonly ReportGroupCodeAndDescription[]>(
			'api/report-codes/groups',
		);
	}

	public async isReportComplete(
		reportId: number,
	): Promise<ReportStatusResponse> {
		const url =
			GenericApi.apiServerURL +
			'api/report/' +
			reportId +
			'/isComplete?' +
			GenericApi.getParamsString({}, [
				ParamType.FORM,
				ParamType.DIVISION,
			]);

		return await FetchUtils.getJSONWithParamsInURL<ReportStatusResponse>(
			url,
		);
	}

	public getReportOutputBlobUrl(
		reportId: number,
		extraParams?: APIParameters,
	): Promise<string> {
		return this.getReportOutputDownloadSpec(reportId, extraParams).then(
			(spec) => spec.url,
		);
	}

	public getReportOutputDownloadSpec(
		reportId: number,
		extraParams?: APIParameters,
	): Promise<DownloadSpec> {
		return FetchUtils.get(
			this.getReportOutputEndpoint(reportId),
			extraParams,
			{ extractParamsFromURL: true },
		).then((response) => this.convertResponseToDownloadSpec(response));
	}

	private getReportOutputEndpoint(
		reportId: number,
		extraParams?: APIParameters,
	) {
		return (
			GenericApi.apiServerURL +
			'api/report-history/' +
			reportId +
			'/output' +
			'?' +
			GenericApi.getParamsString(extraParams, [
				ParamType.FORM,
				ParamType.DIVISION,
			])
		);
	}

	public isHelpAvailable(reportCode: string): Promise<boolean> {
		return FetchUtils.getJSON('api/report/:reportCode/help/available', {
			reportCode,
		}).then(GenericApi.getJSON);
	}

	public getReportOutputHelpBlobUrl(reportId: string): Promise<string> {
		return FetchUtils.get(
			this.getReportHelpEndpoint(reportId),
			{},
			{ extractParamsFromURL: true },
		)
			.then((response) => response.blob())
			.then((blob) => URL.createObjectURL(blob));
	}

	private getReportHelpEndpoint(reportCode: string) {
		const defaultParams = {};
		defaultParams[ParamType.FORM] = GenericApi.formName;
		defaultParams[ParamType.DIVISION] = GenericApi.getDivision();
		return FetchUtils.buildUriAndParams(
			GenericApi.apiServerURL + 'api/report/:reportCode/help',
			{ reportCode },
			defaultParams,
		);
	}

	public getDynamicFieldsForReport(
		reportCode: string,
	): Promise<DynamicFieldsConfig> {
		return FetchUtils.getJSON(
			'api/report/:reportCode/pre-insert/dynamic-fields',
			{ reportCode },
		).then(GenericApi.getJSON);
	}

	public getDynamicFieldsRecord(
		recordId: number,
	): Promise<DynamicFieldsConfig> {
		return FetchUtils.getJSON(
			'api/report-history/:recordId/dynamic-fields',
			{ recordId },
		).then(GenericApi.getJSON);
	}

	public async isShowAddNewReport(): Promise<boolean> {
		const response = await FetchUtils.getJSON(
			'api/report/show-add-new-report',
		);
		if (response !== true && response !== false) {
			throw new Error(
				'show-add-new-report endpoint did not return a boolean as expected.',
			);
		}
		return response;
	}
}
